import type { Die } from '../models/die.type';

/**
 * Where a die is this turn. Every die the active player has is in exactly one
 * place, and moving it is changing that place -- so a die cannot be dropped
 * between two lists, or end up in both.
 */
export type Place =
  | { in: 'board' }
  | { in: 'tray' }
  /** In a technique, by its title: granted techniques are made afresh each time. */
  | { in: 'slot'; technique: string; slots: Array<number> }
  | { in: 'used' };

type Entry = { die: Die; place: Place; order: number };

/** A die and its place, as undo keeps them. */
export type LedgerSnapshot = Array<{ die: Die; place: Place }>;

/**
 * The active player's dice for the turn, and where each one is. The board,
 * the tray and each technique's slots are all read from here.
 */
export class DiceLedger {
  private entries = new Map<string, Entry>();
  /** Bumped on every move, so each place lists its dice in the order they came. */
  private count = 0;

  add(die: Die, place: Place) {
    this.entries.set(die.id, { die, place, order: this.count++ });
  }

  /** Moves a die to a new place. A die the ledger does not hold is ignored. */
  move(die: Die, place: Place) {
    const entry = this.entries.get(die.id);
    if (entry) {
      entry.place = place;
      entry.order = this.count++;
    }
  }

  placeOf(die: Die): Place | undefined {
    return this.entries.get(die.id)?.place;
  }

  has(die: Die) {
    return this.entries.has(die.id);
  }

  /** The dice in one kind of place, in the order they came there. */
  in(kind: Place['in']): Array<Die> {
    return [...this.entries.values()]
      .filter((entry) => entry.place.in == kind)
      .sort((a, b) => a.order - b.order)
      .map((entry) => entry.die);
  }

  /** Every die the ledger holds. */
  all(): Array<Die> {
    return [...this.entries.values()].map((entry) => entry.die);
  }

  /** The die in each of a technique's slots, by slot; undefined where empty. */
  slotsOf(title: string, count: number): Array<Die | undefined> {
    const slots: Array<Die | undefined> = new Array(count).fill(undefined);
    this.entries.forEach(({ die, place }) => {
      if (place.in == 'slot' && place.technique == title) {
        place.slots.forEach((slot) => (slots[slot] = die));
      }
    });
    return slots;
  }

  /** The titles of every technique holding a die. */
  techniquesHolding(): Array<string> {
    const titles = new Set<string>();
    this.entries.forEach(({ place }) => {
      if (place.in == 'slot') titles.add(place.technique);
    });
    return [...titles];
  }

  /** Sends every die in these techniques, or in any, to a new place. */
  empty(to: Place, titles?: Array<string>) {
    this.entries.forEach((entry) => {
      if (entry.place.in == 'slot' && (!titles || titles.includes(entry.place.technique))) {
        entry.place = to;
        entry.order = this.count++;
      }
    });
  }

  /** Puts `dice` on the board in place of whatever was there. */
  replaceBoard(dice: Array<Die>) {
    this.entries.forEach((entry, id) => {
      if (entry.place.in == 'board') this.entries.delete(id);
    });
    dice.forEach((die) => this.add(die, { in: 'board' }));
  }

  clear() {
    this.entries.clear();
  }

  /** A copy, dice and all, for undo -- a die's face can change after it. */
  snapshot(): LedgerSnapshot {
    return [...this.entries.values()]
      .sort((a, b) => a.order - b.order)
      .map(({ die, place }) => ({ die: die.clone(), place: { ...place } }));
  }

  /** Back to a snapshot. It is copied again, so it can be restored more than once. */
  restore(snapshot: LedgerSnapshot) {
    this.clear();
    snapshot.forEach(({ die, place }) => this.add(die.clone(), { ...place }));
  }
}
